/**
 * ============================================================================
 *  LE TICKET DE LA COMMANDE, A ACCROCHER AU SAC (25/10/2026)
 * ============================================================================
 *
 *  Le responsable commande imprime un ticket sur l'imprimante thermique du
 *  magasin (Gprinter GP-U80300II, 80 mm, ESC/POS). L'imprimante est branchee
 *  en Ethernet sur la box ; le telephone Android lui parle par l'appli
 *  RawBT, qui recoit le ticket deja mis en forme et l'envoie tel quel.
 *
 *  Le ticket reprend celui de Glovo : le CODE DE COLLECTE en tres gros, le
 *  numero de commande, l'heure de collecte, le nom du client — jamais son
 *  telephone ni son adresse —, les articles et le total, puis qui a prepare.
 *
 *  Les articles retires de la commande n'y figurent pas. La volaille pesee
 *  porte son poids reel.
 *
 *  42 caracteres par ligne : c'est la largeur la plus etroite que permet
 *  l'imprimante (interrupteur SW-5). Ainsi le ticket reste propre quel que
 *  soit son reglage.
 *
 *  Les accents sont retires : les imprimantes thermiques chinoises n'ont pas
 *  toutes la meme table de caracteres, et un « é » mal compris imprime un
 *  caractere chinois. Un ticket lisible vaut mieux qu'un ticket accentue.
 * ============================================================================
 */

const LARGEUR = 42;

const ESC = 0x1b;
const GS = 0x1d;
const FS = 0x1c;

/** Le texte en ASCII pur : accents retires, signes typographiques remplaces. */
function ascii(texte: string): string {
  return String(texte ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[’‘`´]/g, "'")
    .replace(/[“”«»]/g, '"')
    .replace(/[–—]/g, "-")
    .replace(/[×]/g, "x")
    .replace(/⁠|​/g, "")
    .replace(/[^\x20-\x7e\n]/g, "");
}

/** Coupe un texte en lignes de `largeur` caracteres, sur les espaces. */
function couper(texte: string, largeur: number): string[] {
  const mots = ascii(texte).split(/\s+/).filter(Boolean);
  const lignes: string[] = [];
  let courante = "";
  for (const mot of mots) {
    if (!courante) {
      courante = mot;
    } else if ((courante + " " + mot).length <= largeur) {
      courante += " " + mot;
    } else {
      lignes.push(courante);
      courante = mot;
    }
    while (courante.length > largeur) {
      lignes.push(courante.slice(0, largeur));
      courante = courante.slice(largeur);
    }
  }
  if (courante) lignes.push(courante);
  return lignes.length ? lignes : [""];
}

const dh = (centimes: number) => (Number(centimes || 0) / 100).toFixed(2);

function heure(iso: any): string {
  if (!iso) return "";
  const d =
    typeof iso?.toDate === "function"
      ? iso.toDate()
      : typeof iso?._seconds === "number"
        ? new Date(iso._seconds * 1000)
        : new Date(iso);
  if (isNaN(d.getTime())) return "";
  // Heure du Maroc = UTC.
  return d.toISOString().slice(11, 16);
}

export type TicketCommande = {
  orderCode?: string;
  externalId?: string;
  customerName?: string;
  estimatedPickupTime?: any;
  paymentMethod?: string;
  metadata?: any;
  pickUpCode?: string;
  items?: any[];
};

/** Les lignes d'articles qui partent vraiment dans le sac. */
function articlesDuSac(items: any[]): any[] {
  return (items || []).filter((it) => {
    const st = String(it?.retrait?.statut || "");
    return st !== "RETIRE_GLOVO" && st !== "A_RETIRER_TABLETTE";
  });
}

/** Le ticket en clair : sert a l'apercu et a la mise en forme. */
export function lignesDuTicket(
  commande: TicketCommande,
  opts: { magasin?: string; preparePar?: string; maintenant?: Date } = {},
) {
  const code = String(commande.metadata?.pick_up_code || commande.pickUpCode || "").trim();
  const numero = String(commande.orderCode || commande.externalId || "").trim();
  const articles = articlesDuSac(commande.items || []);
  // Comme Glovo : le nombre de LIGNES, pas d'unites.
  const nbProduits = articles.length;

  const lignesArticles: string[] = [];
  let total = 0;
  for (const it of articles) {
    const q = Math.max(1, Number(it.quantity) || 1);
    const ligne = (Number(it.price) || 0) * q;
    total += ligne;
    const poids = Number(it.poidsReelKg) > 0 ? ` (${String(Number(it.poidsReelKg).toFixed(2))} kg)` : "";
    const prix = dh(ligne);
    const prefixe = `${q} x `;
    const largeurNom = LARGEUR - prefixe.length - prix.length - 1;
    const morceaux = couper(`${it.productName || it.name || "Article"}${poids}`, largeurNom);
    morceaux.forEach((m, i) => {
      if (i === 0) {
        const debut = prefixe + m;
        lignesArticles.push(debut + " ".repeat(Math.max(1, LARGEUR - debut.length - prix.length)) + prix);
      } else {
        lignesArticles.push(" ".repeat(prefixe.length) + m);
      }
    });
  }

  const maintenant = opts.maintenant || new Date();
  const quand = `${String(maintenant.getUTCDate()).padStart(2, "0")}/${String(
    maintenant.getUTCMonth() + 1,
  ).padStart(2, "0")} ${maintenant.toISOString().slice(11, 16)}`;

  return {
    magasin: ascii(opts.magasin || ""),
    code,
    numero,
    collecte: heure(commande.estimatedPickupTime),
    client: ascii(commande.customerName || ""),
    nbProduits,
    lignesArticles,
    total: dh(total),
    pied: ascii(`Prepare par ${opts.preparePar || "-"} - ${quand}`),
  };
}

/** Les octets ESC/POS du ticket, prets pour l'imprimante. */
export function octetsDuTicket(
  commande: TicketCommande,
  opts: { magasin?: string; preparePar?: string } = {},
): Uint8Array {
  const t = lignesDuTicket(commande, opts);
  const out: number[] = [];
  const brut = (...b: number[]) => out.push(...b);
  const texte = (s: string) => {
    for (const c of ascii(s)) out.push(c.charCodeAt(0));
  };
  const ligne = (s = "") => {
    texte(s);
    brut(0x0a);
  };
  const trait = () => ligne("-".repeat(LARGEUR));
  const centre = () => brut(ESC, 0x61, 1);
  const gauche = () => brut(ESC, 0x61, 0);
  const gras = (oui: boolean) => brut(ESC, 0x45, oui ? 1 : 0);
  const taille = (n: number) => brut(GS, 0x21, n); // 0x00 normal, 0x11 double, 0x33 x4

  brut(ESC, 0x40); // remise a zero
  brut(FS, 0x2e); // sortir du mode caracteres chinois (SW-4)
  brut(ESC, 0x74, 0); // table de caracteres standard

  centre();
  gras(true);
  taille(0x11);
  ligne("NATURA BELDI");
  taille(0);
  gras(false);
  if (t.magasin) ligne(t.magasin);
  gauche();
  trait();

  centre();
  ligne("CODE DE COLLECTE");
  if (t.code) {
    gras(true);
    taille(0x33);
    ligne(t.code);
    taille(0);
    gras(false);
  }
  if (t.collecte) ligne(`Collecte estimee : ${t.collecte}`);
  if (t.numero) {
    gras(true);
    ligne(`Commande ${t.numero}`);
    gras(false);
  }
  gauche();
  trait();

  if (t.client) {
    centre();
    gras(true);
    ligne(t.client);
    gras(false);
    gauche();
    trait();
  }

  gras(true);
  ligne(`${t.nbProduits} produit${t.nbProduits > 1 ? "s" : ""}`);
  gras(false);
  for (const l of t.lignesArticles) ligne(l);
  trait();

  gras(true);
  const totalTxt = `${t.total} MAD`;
  ligne("TOTAL" + " ".repeat(Math.max(1, LARGEUR - 5 - totalTxt.length)) + totalTxt);
  gras(false);
  trait();
  ligne(t.pied);

  brut(0x0a, 0x0a, 0x0a, 0x0a);
  brut(GS, 0x56, 66, 0); // avance puis coupe le papier
  return new Uint8Array(out);
}

function enBase64(octets: Uint8Array): string {
  let s = "";
  for (let i = 0; i < octets.length; i++) s += String.fromCharCode(octets[i]);
  return btoa(s);
}

/** Vrai sur un telephone ou une tablette Android. */
export function surAndroid(): boolean {
  return typeof navigator !== "undefined" && /android/i.test(navigator.userAgent || "");
}

/**
 * Envoie le ticket a l'appli RawBT, qui l'imprime sur l'imprimante du
 * magasin. Retourne false si l'appareil ne peut pas imprimer ainsi.
 */
export function imprimerTicket(
  commande: TicketCommande,
  opts: { magasin?: string; preparePar?: string } = {},
): boolean {
  if (!surAndroid()) return false;
  const b64 = enBase64(octetsDuTicket(commande, opts));
  // L'intent Android : si RawBT n'est pas installe, Chrome ouvre sa page
  // sur le Play Store au lieu d'echouer en silence.
  const url = `intent:base64,${b64}#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;`;
  // L'APPLI INSTALLEE SUR L'ECRAN D'ACCUEIL (25/10/2026). En plein ecran,
  // Chrome ignore en silence un changement d'adresse vers « intent: » : le
  // bouton ne faisait rien. Un vrai lien clique, lui, est suivi — dans
  // l'appli installee comme dans Chrome.
  const lien = document.createElement("a");
  lien.href = url;
  lien.rel = "noopener";
  lien.style.display = "none";
  document.body.appendChild(lien);
  lien.click();
  setTimeout(() => lien.remove(), 1000);
  return true;
}
